import { Button } from '@emailforge/ui';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

type LegalPageProps = {
  title: string;
  subtitle?: string;
  effectiveDate?: string;
  children: React.ReactNode;
};

export function LegalPage({ title, subtitle, effectiveDate, children }: LegalPageProps) {
  const [params] = useSearchParams();
  const printMode = params.get('print') === '1';

  useEffect(() => {
    if (!printMode) return;
    // Wait one frame so fonts/styles settle, then trigger the browser's print
    // dialog. The user picks "Save as PDF" from there. Honored by Chromium,
    // Safari, Firefox.
    const id = window.requestAnimationFrame(() => window.print());
    return () => window.cancelAnimationFrame(id);
  }, [printMode]);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '48px 24px 80px' }}>
      <div
        className="mono"
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.08,
          color: 'var(--flame-2)',
          marginBottom: 8,
        }}
      >
        Legal
      </div>
      <h1 className="display" style={{ fontSize: 40, margin: '0 0 8px', letterSpacing: -0.02 }}>
        {title}
      </h1>
      {subtitle && (
        <p style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0 }}>{subtitle}</p>
      )}
      {effectiveDate && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 14,
            gap: 12,
          }}
        >
          <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            Effective date · {effectiveDate}
          </div>
          <Button variant="ghost" size="sm" data-print-hide
            onClick={() => window.print()}
            style={{ flexShrink: 0 }}
          >
            Download as PDF
          </Button>
        </div>
      )}
      <div
        style={{
          marginTop: 36,
          fontSize: 14.5,
          lineHeight: 1.7,
          color: 'var(--ink-2)',
        }}
        className="legal-body"
      >
        {children}
      </div>
    </div>
  );
}

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <h2
        style={{
          fontSize: 19,
          fontWeight: 600,
          margin: '0 0 10px',
          color: 'var(--ink)',
          letterSpacing: -0.01,
        }}
      >
        {title}
      </h2>
      <div>{children}</div>
    </section>
  );
}

export function P({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: '0 0 12px' }}>{children}</p>;
}

export function UL({ children }: { children: React.ReactNode }) {
  return <ul style={{ margin: '0 0 14px', paddingLeft: 22 }}>{children}</ul>;
}
